import type { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import { resolveAssistantTaskNoteReferenceSyncModelUuid } from "@/modules/uuid/sync-models/resolve-assistant-task-note-reference-sync-model-uuid";
import type { AssistantTaskModelData } from "@/store/assistant/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { buildAssistantTaskNoteReferenceOptimisticUpsert } from "@/store/sync/operations/helpers/assistant-tasks/build-assistant-task-note-reference-optimistic-updates";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import type { ICreateAssistantTaskOperation } from "@/store/sync/operations/types";
import type { OptimisticSyncUpdate, SyncModelData } from "@/store/sync/types";

export class CreateAssistantTaskOperation extends BaseSyncOperation<ICreateAssistantTaskOperation> {
  get operationKind(): "CREATE_ASSISTANT_TASK" {
    return "CREATE_ASSISTANT_TASK";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    return [
      this.generateTaskUpdate(),
      ...this.referencedNoteIds.map(noteId =>
        buildAssistantTaskNoteReferenceOptimisticUpsert({
          assistantId: this.payload.assistant_id,
          committedAt: this.committedAt,
          modelScopes: [generateDefaultOwnerScopes({ store: this.store })],
          noteId,
          optimisticUpdateId: uuidModule.generate(),
          spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
          taskId: this.payload.id,
        })
      ),
    ];
  }

  async triggerRecompute(): Promise<void> {
    await this.store.assistantTasks.recompute(this.payload.id);
    if (this.referencedNoteIds.length > 0) {
      await this.store.assistantTaskNoteReferences.bulkRecompute(
        this.referencedNoteIds.map(noteId =>
          resolveAssistantTaskNoteReferenceSyncModelUuid({
            taskId: this.payload.id,
            noteId,
          })
        )
      );
    }
  }

  private generateTaskUpdate(): OptimisticSyncUpdate<AssistantTaskModelData> {
    return {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: "ASSISTANT_TASK",
        model_version: 1,
        model_data: {
          space_account_id: this.store.spaceAccounts.myPersonalSpaceAccountId,
          assistant_id: this.payload.assistant_id,
          project_id: this.payload.project_id ?? null,
          title: this.payload.title,
          description: this.payload.description ?? null,
          status: this.payload.status,
          due_at: this.payload.due_at ?? null,
          created_at: this.committedAt,
          updated_at: this.committedAt,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    } as unknown as OptimisticSyncUpdate<AssistantTaskModelData>;
  }

  private get referencedNoteIds(): Uuid[] {
    return [...new Set(this.payload.referenced_note_ids ?? [])];
  }
}
